import React from 'react';
import { useFormContext } from 'react-hook-form';

import { Accordion, AccordionContent, AccordionItem, Input } from '@column/ui';

import { LabeledOption } from '../_util/entityOptions';
import { IdentificationDocument, PersonEntityFormValues } from '../_util/useEntityForm';
import { FormField } from '~/components';

import { CountrySelect, REVEAL_ITEM_CLASS, SensitiveInput, SplitRow } from './EntityFieldInputs';

// Non-U.S. identification documents, in the order the person form has always shown them. The
// backend rejects a U.S. issuing country for each, so those options are filtered out of the picker.
const FOREIGN_DOCS: {
  id: 'driversLicense' | 'passport' | 'nationalId';
  label: string;
  placeholder: string;
  excludeCountries: string[];
}[] = [
  {
    id: 'driversLicense',
    label: 'Drivers License',
    placeholder: 'Drivers License Number',
    excludeCountries: ['US', 'CA'],
  },
  { id: 'passport', label: 'Passport', placeholder: 'Passport Number', excludeCountries: ['US'] },
  { id: 'nationalId', label: 'National ID', placeholder: 'National ID Number', excludeCountries: ['US', 'CA'] },
];

// A U.S. citizen supplies an SSN/ITIN, a non-citizen at least one foreign document; neither group
// shows until the citizenship answer is known. Shared by the entity page and the link-person modal.
const INSTANT = { duration: 0 };

export const PersonIdentificationFields: React.FC<{
  isCitizen?: boolean;
  // Animate the groups in and out; otherwise they swap in place (e.g. for a default or loaded answer).
  animate?: boolean;
  countryOptions: LabeledOption[];
  disabled?: boolean;
  // SSN is shown in the clear while creating (nothing to hide yet) and masked by default on edit.
  ssnDefaultVisible?: boolean;
}> = ({ isCitizen, animate, countryOptions, disabled, ssnDefaultVisible }) => {
  const form = useFormContext<PersonEntityFormValues>();
  const transition = animate ? undefined : INSTANT;

  return (
    <>
      <Accordion type="single" value={isCitizen === true ? 'ssn' : ''}>
        <AccordionItem value="ssn" className={REVEAL_ITEM_CLASS}>
          <AccordionContent className="p-1" transition={transition}>
            <FormField<PersonEntityFormValues>
              id="ssn"
              label="Social Security Number"
              description="ITIN is also acceptable."
              rules={{ required: isCitizen === true }}
            >
              {({ value, onChange, onBlur }, { error }) => (
                <SensitiveInput
                  value={(value as string) ?? ''}
                  onChange={onChange}
                  onBlur={onBlur}
                  placeholder="Social Security Number"
                  label="Social Security Number"
                  defaultVisible={ssnDefaultVisible}
                  isDisabled={disabled}
                  hasError={!!error}
                />
              )}
            </FormField>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <Accordion type="single" value={isCitizen === false ? 'documents' : ''}>
        <AccordionItem value="documents" className={REVEAL_ITEM_CLASS}>
          <AccordionContent className="p-1" transition={transition}>
            {/* Single column so each field keeps its full width — a full-width FormField is what trips
                the container query that puts the label to the left of the input. */}
            <div className="grid gap-6">
              {FOREIGN_DOCS.map((doc) => (
                <FormField<PersonEntityFormValues> key={doc.id} id={doc.id} label={doc.label}>
                  {({ value, onBlur }) => (
                    <SplitRow>
                      <Input
                        value={(value as IdentificationDocument)?.number ?? ''}
                        onChange={(event) =>
                          form.setValue(`${doc.id}.number`, event.target.value, { shouldDirty: true })
                        }
                        onBlur={onBlur}
                        placeholder={doc.placeholder}
                        disabled={disabled}
                      />
                      <CountrySelect
                        value={(value as IdentificationDocument)?.countryCode}
                        onChange={(next) => form.setValue(`${doc.id}.countryCode`, next, { shouldDirty: true })}
                        options={countryOptions.filter((c) => !doc.excludeCountries.includes(c.value))}
                        placeholder="Issuing country..."
                        isDisabled={disabled}
                      />
                    </SplitRow>
                  )}
                </FormField>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </>
  );
};
